"use client"

import Link from "next/link"
import { useEffect, useRef, useState } from "react"

import type { ApiErrorPayload } from "@/lib/api/api-envelope"
import {
  ENTITLEMENT_BLOCKER,
  entitlementBlockerDefaultUserMessage,
  isEntitlementBlockerCode,
  type EntitlementBlockerCode,
} from "@/features/billing/entitlement-reason-codes"
import {
  billingLifecycleWantsNewSubscriptionCheckout,
  lifecycleAllowsPaidCatalogEntitlements,
} from "@/features/billing/app-billing-lifecycle"
import { getPlanDefinition, isSelfServePaidPlan, type PlanId } from "@/features/billing/plans"
import { requestBillingPortalUrl } from "@/lib/billing/billing-portal-client"
import {
  requestNewSelfServeSubscriptionCheckout,
  requestSelfServePlanChangeCheckout,
} from "@/lib/billing/billing-checkout-client"
import { requestStorageChunkCheckoutUrl } from "@/lib/billing/billing-storage-chunk-checkout-client"
import {
  planBillingCheckoutSuccessPath,
  planBillingManageHref,
} from "@/lib/billing/plan-billing-routes"
import { formatStorageBytes } from "@/lib/billing/usage-math"
import { trackBillingLimitHit } from "@/lib/analytics/billing-analytics"
import { useBillingSummary } from "@/hooks/use-billing-summary"
import { Button } from "@/components/ui/button"
import { formatUsdWholeFromCents } from "@/lib/billing/format-usd"
import { cn } from "@/lib/utils"

function headline(code: string): string {
  if (!isEntitlementBlockerCode(code)) return "Request blocked"
  const m: Record<EntitlementBlockerCode, string> = {
    [ENTITLEMENT_BLOCKER.EVENT_LIMIT_REACHED]: "Event limit reached",
    [ENTITLEMENT_BLOCKER.MEDIA_ITEM_LIMIT_REACHED]: "Media limit reached",
    [ENTITLEMENT_BLOCKER.STORAGE_LIMIT_REACHED]: "You've reached your storage limit",
    [ENTITLEMENT_BLOCKER.FILE_TOO_LARGE]: "File too large for your plan",
    [ENTITLEMENT_BLOCKER.EVENT_ALBUM_LIMIT_REACHED]: "Album is full",
    [ENTITLEMENT_BLOCKER.STORAGE_ADD_ON_NOT_OFFERED]: "Storage add-ons unavailable",
    [ENTITLEMENT_BLOCKER.STORAGE_ADD_ON_LIMIT_REACHED]: "Storage add-on limit reached",
  }
  return m[code]
}

function formatLimitLine(
  code: EntitlementBlockerCode,
  used?: number,
  limit?: number,
  mediaKind?: "image" | "video",
): string | null {
  if (used === undefined || limit === undefined) return null
  if (
    code === ENTITLEMENT_BLOCKER.MEDIA_ITEM_LIMIT_REACHED &&
    limit === 0 &&
    mediaKind === "video"
  ) {
    return "Video uploads are not included on your current plan (0 allowed)."
  }
  if (code === ENTITLEMENT_BLOCKER.STORAGE_LIMIT_REACHED) {
    return `${formatStorageBytes(used)} used of ${formatStorageBytes(limit)} included`
  }
  if (code === ENTITLEMENT_BLOCKER.FILE_TOO_LARGE) {
    return `This file is ${formatStorageBytes(used)}; maximum allowed is ${formatStorageBytes(limit)}`
  }
  return `${used.toLocaleString()} used of ${limit.toLocaleString()} allowed`
}

export type BillingEntitlementBlockedProps = {
  payload: ApiErrorPayload
  compact?: boolean
  className?: string
  analyticsSurface?: string
}

export function BillingEntitlementBlocked({
  payload,
  compact,
  className,
  analyticsSurface,
}: BillingEntitlementBlockedProps) {
  const { data: billing } = useBillingSummary()
  const limitHitSig = useRef("")
  const [busy, setBusy] = useState<"portal" | "checkout" | "storage" | null>(
    null,
  )
  const [err, setErr] = useState<string | null>(null)
  const b0 = payload.blockers?.[0]
  const code: EntitlementBlockerCode = isEntitlementBlockerCode(payload.code)
    ? payload.code
    : b0 && isEntitlementBlockerCode(b0.code)
      ? b0.code
      : ENTITLEMENT_BLOCKER.MEDIA_ITEM_LIMIT_REACHED
  const used = b0?.used
  const limit = b0?.limit
  const upgrade = b0?.upgradePlan
  const upgradePlanDef =
    upgrade && isSelfServePaidPlan(upgrade) ? getPlanDefinition(upgrade) : null
  const upgradePriceSuffix = formatUsdWholeFromCents(
    upgradePlanDef?.monthlyPriceCents ?? null,
  )
  const planId: PlanId = billing?.plan ?? "free"
  const planDef = getPlanDefinition(planId)
  const phase = billing?.rollout?.phase ?? 1
  const commerce = Boolean(billing?.rollout?.commerce)
  const storageAddonRollout = Boolean(billing?.rollout?.storageAddons)
  const blockerUx = phase >= 2
  const canBuyStorageChunk =
    commerce &&
    storageAddonRollout &&
    billing?.lifecycle != null &&
    lifecycleAllowsPaidCatalogEntitlements(billing.lifecycle) &&
    planDef.storageAddOn.maxSelfServeChunks > 0 &&
    code === ENTITLEMENT_BLOCKER.STORAGE_LIMIT_REACHED

  const openPortal = async () => {
    setErr(null)
    setBusy("portal")
    try {
      const r = await requestBillingPortalUrl({
        surface: analyticsSurface ?? "entitlement_blocker",
      })
      if (r.ok) window.location.href = r.url
      else setErr(r.message)
    } finally {
      setBusy(null)
    }
  }

  const startStorageChunkCheckout = async () => {
    setErr(null)
    setBusy("storage")
    try {
      const r = await requestStorageChunkCheckoutUrl()
      if (r.ok) window.location.href = r.url
      else setErr(r.message)
    } finally {
      setBusy(null)
    }
  }

  const startUpgrade = async (target: "pro" | "business") => {
    setErr(null)
    setBusy("checkout")
    try {
      const useNew =
        !billing ||
        billingLifecycleWantsNewSubscriptionCheckout(billing.lifecycle)
      const r = useNew
        ? await requestNewSelfServeSubscriptionCheckout({
            plan: target,
            interval: "monthly",
          })
        : await requestSelfServePlanChangeCheckout({
            plan: target,
            interval: "monthly",
          })
      if (r.ok && "url" in r && r.url) window.location.href = r.url
      else if (r.ok && "applied" in r && r.applied) {
        window.location.href = planBillingCheckoutSuccessPath()
      } else if (!r.ok) setErr(r.message)
    } finally {
      setBusy(null)
    }
  }

  const line = formatLimitLine(code, used, limit, b0?.mediaKind)
  const title = headline(code)

  useEffect(() => {
    if (
      code !== ENTITLEMENT_BLOCKER.EVENT_LIMIT_REACHED &&
      code !== ENTITLEMENT_BLOCKER.MEDIA_ITEM_LIMIT_REACHED &&
      code !== ENTITLEMENT_BLOCKER.STORAGE_LIMIT_REACHED
    ) {
      return
    }
    const sig = `${code}-${analyticsSurface ?? ""}-${billing?.plan ?? ""}-${billing?.lifecycle ?? ""}`
    if (limitHitSig.current === sig) return
    limitHitSig.current = sig
    trackBillingLimitHit({
      blockerCode: code,
      surface: analyticsSurface ?? "unknown",
      planId: billing?.plan,
      lifecycle: billing?.lifecycle,
    })
  }, [analyticsSurface, billing?.lifecycle, billing?.plan, code])

  return (
    <div
      className={cn(
        "rounded-2xl border border-destructive/35 bg-destructive/10 px-3 py-3 text-foreground sm:px-4 sm:py-4",
        compact ? "text-left" : "",
        className,
      )}
      role="alert"
    >
      <p className="type-body-sm font-semibold text-foreground">{title}</p>
      {line ? (
        <p className="mt-1 type-caption tabular-nums text-muted-foreground">{line}</p>
      ) : (
        <p className="mt-1 type-caption text-muted-foreground">
          {payload.message?.trim() || entitlementBlockerDefaultUserMessage(code)}
        </p>
      )}
      {b0?.mediaKind ? (
        <p className="mt-1 type-caption text-muted-foreground">
          Applies to {b0.mediaKind === "video" ? "video" : "image"} uploads.
        </p>
      ) : null}
      {err ? (
        <p className="mt-2 type-caption text-destructive">{err}</p>
      ) : null}
      {!blockerUx ? (
        <div className="mt-3">
          <Button type="button" size="sm" variant="ghost" className="w-full sm:w-auto" asChild>
            <Link href={planBillingManageHref()}>Manage plan</Link>
          </Button>
        </div>
      ) : (
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {blockerUx && !commerce && upgrade && isSelfServePaidPlan(upgrade) ? (
          <p className="type-caption text-foreground">
            Self-serve checkout is not enabled for this deployment.
          </p>
        ) : null}
        {commerce && upgrade && isSelfServePaidPlan(upgrade) ? (
          <Button
            type="button"
            size="sm"
            className="w-full sm:w-auto"
            disabled={busy !== null}
            onClick={() => void startUpgrade(upgrade)}
          >
            {busy === "checkout"
              ? "Opening checkout…"
              : `Upgrade to ${upgradePlanDef?.displayName ?? upgrade}${
                  upgradePriceSuffix ? ` (${upgradePriceSuffix}/mo)` : ""
                }`}
          </Button>
        ) : upgrade === "enterprise" ? (
          <Button type="button" size="sm" variant="secondary" asChild>
            <Link href="/pricing#enterprise">Contact for Enterprise</Link>
          </Button>
        ) : null}
        {canBuyStorageChunk ? (
          <>
            <Button
              type="button"
              size="sm"
              className="w-full sm:w-auto"
              disabled={busy !== null}
              onClick={() => void startStorageChunkCheckout()}
            >
              {busy === "storage"
                ? "Opening checkout…"
                : "Add storage (recurring block)"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={busy !== null}
              onClick={() => void openPortal()}
            >
              {busy === "portal" ? "Opening portal…" : "Billing portal"}
            </Button>
          </>
        ) : null}
        <Button type="button" size="sm" variant="ghost" className="w-full sm:w-auto" asChild>
          <Link href={planBillingManageHref()}>Manage plan</Link>
        </Button>
      </div>
      )}
      {(code === ENTITLEMENT_BLOCKER.MEDIA_ITEM_LIMIT_REACHED ||
        code === ENTITLEMENT_BLOCKER.STORAGE_LIMIT_REACHED) && (
        <p className="mt-3 type-caption text-muted-foreground">
          Remove photos, videos, or files from your events to free capacity, then try again.
        </p>
      )}
    </div>
  )
}
